import React, { useEffect, useState } from 'react';

import type { BackendHealthState } from '../utils/backendHealth';
import { backendHealth } from '../utils/backendHealth';

/**
 * Tiny status banner that surfaces backend connectivity issues to the user.
 * Hidden by default; mounts only when `isDown` is true. Intentionally minimal
 * — wiring it into App.tsx layout is a follow-up task per the Stream D plan.
 */
/** Routes rendered without the Layout shell (no fixed topbar). */
const SHELL_LESS_PREFIXES = ['/login', '/auth/callback', '/video/welcome'];

function hasAppShell(): boolean {
  if (typeof window === 'undefined') return true;
  const path = window.location.pathname;
  return !SHELL_LESS_PREFIXES.some(prefix => path.startsWith(prefix));
}

export function BackendStatusBanner(): React.ReactElement | null {
  const [state, setState] = useState<BackendHealthState>(() =>
    backendHealth.getState()
  );

  useEffect(() => {
    const unsubscribe = backendHealth.subscribe(setState);
    return unsubscribe;
  }, []);

  if (!state.isDown) return null;

  return (
    <div
      role='status'
      aria-live='polite'
      data-testid='backend-status-banner'
      style={{
        position: 'fixed',
        // Sit just below the fixed h-16 (4rem) topbar so the banner never
        // obscures the header row (audit finding: it used to cover it).
        // The banner is mounted globally in App.tsx, so on shell-less
        // routes (login / auth callback / kiosk welcome) there is no
        // topbar and it hugs the viewport top instead.
        top: hasAppShell() ? '4rem' : 0,
        left: 0,
        right: 0,
        zIndex: 9,
        padding: '8px 16px',
        background: '#fde68a',
        color: '#78350f',
        textAlign: 'center',
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        borderBottom: '1px solid #d97706',
      }}
    >
      Connection issues — reconnecting…
    </div>
  );
}

export default BackendStatusBanner;
